import { useEffect, useRef, useState, useMemo } from 'react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Row from 'react-bootstrap/Row'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import CalendarPicker from '../components/calendar/CalendarPicker'
import JobDetails from '../components/card/JobDetails'
import CompanyDetails from '../components/card/CompanyDetails'
import apiCalendar from '../services/api/calendar'
import {
  FooterContent,
  StyledCol,
  StyledColFlex,
  StyledRow,
  StyledRowAuto,
  CalendarPageWrapper,
  CalendarHeaderRow,
  CalendarPageContent,
  ScheduledDetailsBox,
  ScheduledDetailsLabel,
  ScheduledDetailsText,
} from '../components/styled'
import FullLogo from '../assets/logos/full-logo.png'
import LoadingPage from '../components/loadingPage'
import { formatCountryName } from '../utilities/stringUtilities'
import { decodeInterviewToken } from '../utilities/tokenUtils'
import { track } from '../services/analytics'
import NotificationBoxPage from '../components/notificationBoxPage'
import InterviewTimeSelector from '../components/calendar/InterviewTimeSelector'
import { formatWebsiteUrl } from '../utilities/stringUtilities'
import LanguagesDropdown from '../components/languageDropdown'
import { Col } from 'react-bootstrap'
import TimeCircle from '../assets/svgs/timeCircle'
import Monitor from '../assets/svgs/Monitor'
import ExclamationCircle from '../assets/svgs/exclamationCircle'
import { months } from '../constants/months'
import theme from '../constants/theme'
import {
  InvitationCard,
  InvitationHeader,
  InvitationPill,
  InvitationTitle,
  InvitationSubtitle,
  InvitationBody,
  DateInfoRow,
  DateBox,
  DateBoxMonth,
  DateBoxDay,
  TimeInfoWrapper,
  TimeInfoRow,
  MutedTimeInfoRow,
  AvailableBadge,
  AvailableDot,
  SlotConflictAlert,
} from '../components/calendar/styled'

dayjs.extend(utc)
dayjs.extend(timezone)

function Calendar() {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language
  const navigate = useNavigate()
  const { token } = useParams()
  const today = dayjs()
  const [time, setTime] = useState(
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  )
  const monthLabel =
    months[today.$d.getMonth()]?.[locale] || months[today.$d.getMonth()]?.en
  const fullDate = today.format('dddd, D MMMM')

  let role = null
  let tokenPayload = null
  try {
    if (token) {
      const decodedToken = decodeInterviewToken(token)
      tokenPayload = decodedToken
      role = decodedToken.role
    }
  } catch {
    role = null
  }

  const msgRef = useRef(null)
  const [data, setData] = useState()
  const [isLoaded, setIsLoaded] = useState(false)
  const [invalidLink, setInvalidLink] = useState(false)
  const [selectedDateTime, setSelectedDateTime] = useState(null)
  const [selectedEndDateTime, setSelectedEndDateTime] = useState(null)
  const [allDatesPassed, setAllDatesPassed] = useState(false)
  const [isBooked, setIsBooked] = useState(false)
  const [localAvailableDates, setLocalAvailableDates] = useState(null)
  const [eventId, setEventId] = useState(null)
  const [assignedUserId, AssignedUserId] = useState(null)
  const [isDeclined, setIsDeclined] = useState(false)
  const [isSlotSelectionModalOpen, setIsSlotSelectionModalOpen] =
    useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [bookingError, setBookingError] = useState(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const autoCoverLetterMsg = useRef(null)
  const [scheduledDate, setScheduledDate] = useState(null)
  const [scheduledTime, setScheduledTime] = useState(null)
  const [additionalDetails, setAdditionalDetails] = useState(null)

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(
        new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
      )
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    apiCalendar
      .getSingle(token)
      .then((res) => {
        if (res.type.toLowerCase() === 'form') navigate(`/interview/${token}`)
        setData(res)
        setAdditionalDetails(res.customMessage || res.additionalDetails || null)
        if (res.isDeclined) {
          setIsDeclined(res.isDeclined)
          msgRef.current = t('pages.youDeclined', {
            position: res.position.title,
          })
        }
        if (res.isBooked) {
          setIsBooked(res.isBooked)
          msgRef.current = t('pages.youBookedTime')
          return
        }
        msgRef.current = t('pages.bookDate')
        if (
          res.position?.enableAutoCoverLetter &&
          res.type.toLowerCase() === 'coverletter'
        )
          autoCoverLetterMsg.current = t('calendar.autoCoverLetterMsg', {
            company: res.company?.companyName,
          })

        let allPassed
        const localDates = res?.availableDates?.map((dateEntry) => {
          if (dateEntry && typeof dateEntry === 'object' && dateEntry.start) {
            const start = dayjs
              .utc(dateEntry.start)
              .local()
              .format('YYYY-MM-DD HH:mm:ss')
            const end = dayjs
              .utc(dateEntry.end)
              .local()
              .format('YYYY-MM-DD HH:mm:ss')
            return {
              start,
              end,
              eventId: dateEntry.eventId,
              assignedUserId: dateEntry.assignedUserId,
              isBooked: dateEntry.isBooked,
            }
          }
          const localDate = dayjs
            .utc(dateEntry)
            .local()
            .format('YYYY-MM-DD HH:mm:ss')
          return localDate
        })

        if (localDates)
          allPassed = localDates.every((date) => dayjs(date).isBefore(dayjs()))
        else allPassed = []

        setAllDatesPassed(allPassed)
        setLocalAvailableDates(localDates)
        setIsLoaded(true)
      })
      .catch(() => {
        setInvalidLink(true)
        msgRef.current = t('pages.interviewExpired')
      })
  }, [navigate, t, token, isSlotSelectionModalOpen, refreshNonce])

  const handleDateClick = (date) => {
    if (date && typeof date === 'object' && date.start) {
      setSelectedDateTime(date.start)
      setSelectedEndDateTime(date.end)
      setEventId(date.eventId)
      AssignedUserId(date.assignedUserId)
    } else {
      setSelectedDateTime(date)
      setSelectedEndDateTime(null)
    }
  }

  const handleDecline = () => {
    trackInterviewDeclined()
    return apiCalendar.declineInterview(token).then(() => navigate(0))
  }

  const formatDateTimeRange = (startDateTime, endDateTime) => {
    const formattedDate = dayjs(startDateTime).format('MMMM DD, YYYY')
    const formattedTime = dayjs(startDateTime).format('hh:mm A')
    const formattedEndTime = endDateTime
      ? ` - ${dayjs(endDateTime).format('hh:mm A')}`
      : ''

    return {
      date: formattedDate,
      time: `${formattedTime}${formattedEndTime}`,
    }
  }

  const handleScheduledTypeInterview = (_customMessage) => {
    const { date, time } = formatDateTimeRange(
      selectedDateTime,
      selectedEndDateTime,
    )

    navigate('/scheduled-notification', {
      state: {
        scheduledDate: date,
        scheduledTime: time,
        additionalDetails: _customMessage || null,
      },
    })
  }

  const handleOtherSchedulingStageTypes = (_customMessage) => {
    const { date, time } = formatDateTimeRange(
      selectedDateTime,
      selectedEndDateTime,
    )

    setAdditionalDetails(_customMessage || null)
    setIsBooked(true)
    msgRef.current = t('calendar.interviewBookedSuccess')
    setScheduledDate(date)
    setScheduledTime(time)
  }

  const handleNonSchedulingStage = (interviewToken) => {
    navigate(`/interview/${interviewToken}`)
  }

  const interviewEventProperties = () => ({
    email:
      data?.candidate?.email ||
      data?.applicant?.email ||
      data?.email ||
      tokenPayload?.email,
    user_id:
      data?.candidate?.id ||
      tokenPayload?.candidate_id ||
      tokenPayload?.user_id ||
      tokenPayload?.sub,
    position_name: data?.position?.title,
    agency_name: data?.position?.agency?.name || data?.agency?.name,
    company_name: data?.company?.companyName,
    interview_type: data?.type,
  })

  const trackInterviewScheduled = () => {
    track('Company Profile Completed', interviewEventProperties(), {
      transport: 'sendBeacon',
    })
  }

  const trackInterviewDeclined = () => {
    const properties = interviewEventProperties()
    delete properties.position_name
    track('Decline', properties, { transport: 'sendBeacon' })
  }

  const isRescheduling = () =>
    isBooked ||
    !!data?.isRescheduled ||
    !!data?.rescheduled ||
    !!data?.previousDate ||
    sessionStorage.getItem('interviewRescheduled') === 'true' ||
    localStorage.getItem('interviewRescheduled') === 'true'

  const handleScheduled = (newDate) => {
    if (import.meta.env.DEV)
      // eslint-disable-next-line no-console
      console.log('[analytics] booking', {
        isBooked,
        sessionFlag: sessionStorage.getItem('interviewRescheduled'),
        localFlag: localStorage.getItem('interviewRescheduled'),
        payloadKeys: Object.keys(data || {}),
        payload: data,
      })
    if (isRescheduling()) trackInterviewRescheduled(newDate)
    else trackInterviewScheduled()
  }

  const trackInterviewRescheduled = (newDate) => {
    sessionStorage.removeItem('interviewRescheduled')
    localStorage.removeItem('interviewRescheduled')
    track(
      'Reschedule',
      {
        ...interviewEventProperties(),
        new_date: newDate,
      },
      { transport: 'sendBeacon' },
    )
  }

  const handleSubmit = () => {
    if (selectedDateTime && !isSubmitting) {
      setIsSubmitting(true)
      const formattedDateTime = dayjs(selectedDateTime).format(
        'YYYY-MM-DD HH:mm:ss',
      )
      const utcDateTime = dayjs(selectedDateTime)
        .utcOffset(0)
        .format('YYYY-MM-DDTHH:mm:ss[Z]')

      let formattedEndDateTime = null
      let utcEndDateTime = null

      if (selectedEndDateTime) {
        formattedEndDateTime = dayjs(selectedEndDateTime).format(
          'YYYY-MM-DD HH:mm:ss',
        )
        utcEndDateTime = dayjs(selectedEndDateTime)
          .utcOffset(0)
          .format('YYYY-MM-DDTHH:mm:ss[Z]')
      }

      const requestData = {
        localTime: formattedDateTime,
        utc: utcDateTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        candidateTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }

      if (formattedEndDateTime && utcEndDateTime) {
        requestData.localEndTime = formattedEndDateTime
        requestData.utcEnd = utcEndDateTime
      }

      if (eventId) requestData.eventId = eventId
      if (assignedUserId) requestData.assignedUserId = assignedUserId
      apiCalendar
        .patch(token, requestData)
        .then(({ interviewToken, isSchedulingStage, customMessage }) => {
          setBookingError(null)
          setLocalAvailableDates((prevDates) =>
            prevDates.map((dateEntry) => {
              if (
                dateEntry?.start === selectedDateTime &&
                dateEntry?.end === selectedEndDateTime
              ) {
                return { ...dateEntry, isBooked: true }
              }
              return dateEntry
            }),
          )
          handleScheduled(formattedDateTime)
          if (isSchedulingStage) {
            if (data?.type?.toLowerCase() === 'scheduled') {
              handleScheduledTypeInterview(customMessage)
            } else {
              handleOtherSchedulingStageTypes(customMessage)
            }
          } else {
            handleNonSchedulingStage(interviewToken)
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error submitting interview details:', error)
          const conflict = error?.response?.status === 409
          setLocalAvailableDates((prevDates) =>
            prevDates.map((dateEntry) => {
              if (
                dateEntry?.start === selectedDateTime &&
                dateEntry?.end === selectedEndDateTime
              ) {
                return { ...dateEntry, isBooked: conflict }
              }
              return dateEntry
            }),
          )
          if (conflict) {
            setBookingError(t('calendar.slotAlreadyBooked'))
            setRefreshNonce((n) => n + 1)
          }
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  }

  const companyDetailsData = useMemo(
    () => ({
      location: (data?.company?.workingLocations ?? [])
        .map((loc) => formatCountryName(loc))
        .join(', '),
      size: data?.company?.companySize,
      website: formatWebsiteUrl(data?.company?.website),
      industry: data?.company?.industry,
      logo: data?.company?.logo,
      companyName: data?.company?.companyName,
    }),
    [data],
  )

  const jobDetailsData = useMemo(
    () => ({
      desc: data?.position?.description,
      positionName: data?.position?.title,
      startDate: data?.position?.startDate,
      endDate: data?.position?.endDate,
    }),
    [data],
  )

  if (!isBooked && isDeclined) {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('calendar.declineMsg')}
        rescheduleEnabled={false}
      />
    )
  }

  // Show success notification after just booking (for non-SCHEDULED types like INPERSON)
  if (
    isLoaded &&
    isBooked &&
    data.type.toLowerCase() !== 'inperson' &&
    data.type.toLowerCase() !== 'scheduled' &&
    (scheduledDate || scheduledTime || msgRef.current !== t('pages.bookDate'))
  ) {
    return (
      <NotificationBoxPage
        messageType="success"
        message={msgRef.current}
        rescheduleEnabled={false}
      />
    )
  }

  // Show warning if interview was already booked from server (no scheduled details)
  if (
    !invalidLink &&
    isBooked &&
    data.type.toLowerCase() !== 'inperson' &&
    !scheduledDate &&
    !scheduledTime
  ) {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('calendar.alreadyBooked')}
        rescheduleEnabled={false}
      />
    )
  }

  if (!isLoaded && !invalidLink && !isBooked) {
    return <LoadingPage />
  }

  if (
    (allDatesPassed &&
      ['inperson', 'scheduled'].includes(data.type.toLowerCase())) ||
    invalidLink ||
    (data.type.toLowerCase() === 'inperson' &&
      role !== 'interviewer' &&
      role !== 'interviewee' &&
      role !== 'viewer')
  ) {
    return (
      <NotificationBoxPage
        messageType="error"
        message={t('pages.linkExpired')}
        rescheduleEnabled={false}
      />
    )
  }

  if ((role === 'interviewer' || role === 'viewer') && !isBooked) {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('calendar.waiting')}
        rescheduleEnabled={false}
      />
    )
  } else {
    if (data.type.toLowerCase() === 'inperson' && isBooked) {
      return <Navigate to={`/interview/${token}`} replace={true} />
    }
  }

  return (
    <CalendarPageWrapper>
      {/* ========header */}
      <CalendarHeaderRow>
        <Col xs="auto">
          <img height="auto" width="150px" src={FullLogo} alt="" />
        </Col>
        <Col />
        <Col xs="auto" className="mt-3">
          <LanguagesDropdown />
        </Col>
      </CalendarHeaderRow>
      <CalendarPageContent>
        <StyledRow className="calendar-page">
          <StyledCol md={12} lg={6}>
            {!['inperson', 'scheduled'].includes(data?.type?.toLowerCase()) ? (
              <CalendarPicker
                title={msgRef.current}
                token={token}
                endDate={data?.position?.endDate}
                duration={data?.position?.schedulerDuration}
                extraMsg={autoCoverLetterMsg.current}
                companyName={data?.company?.companyName}
                positionName={data?.position?.title}
                type={data?.type}
                onDecline={trackInterviewDeclined}
                onScheduled={handleScheduled}
              />
            ) : (
              <InvitationCard>
                <InvitationHeader>
                  <div>
                    <InvitationPill>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                      {t('interview.interviewInvitation')}
                    </InvitationPill>
                  </div>
                  <InvitationTitle>
                    {`You're invited to interview for ${data?.position?.title} at ${data?.company?.companyName}`}
                  </InvitationTitle>
                  {msgRef.current && (
                    <InvitationSubtitle>{msgRef.current}</InvitationSubtitle>
                  )}
                </InvitationHeader>

                <InvitationBody>
                  <DateInfoRow>
                    <DateBox>
                      <DateBoxMonth>{monthLabel}</DateBoxMonth>
                      <DateBoxDay>{today.$d.getDate()}</DateBoxDay>
                    </DateBox>
                    <TimeInfoWrapper>
                      <TimeInfoRow>
                        <TimeCircle width="16" height="16" />
                        <span>
                          {fullDate} &middot; {time}
                        </span>
                        <AvailableBadge>
                          <AvailableDot />
                          {t('calendar.availableNow') || 'Available now'}
                        </AvailableBadge>
                      </TimeInfoRow>
                      <MutedTimeInfoRow>
                        <Monitor width={16} height={16} color="#6B7280" />
                        <span>
                          {data?.type?.toUpperCase() === 'TEXT'
                            ? 'Text interview'
                            : data?.type?.toUpperCase() === 'VIDEO'
                              ? 'Video interview'
                              : data?.type?.toUpperCase() === 'FORM'
                                ? 'Form interview'
                                : data?.type?.toUpperCase() === 'COVERLETTER'
                                  ? 'Cover letter interview'
                                  : ''}
                        </span>
                      </MutedTimeInfoRow>
                    </TimeInfoWrapper>
                  </DateInfoRow>

                  {bookingError && (
                    <SlotConflictAlert>
                      <ExclamationCircle
                        width="18"
                        height="18"
                        color={theme.talentinoRed}
                      />
                      <span>{bookingError}</span>
                    </SlotConflictAlert>
                  )}
                  {additionalDetails && (
                    <ScheduledDetailsBox>
                      <ScheduledDetailsLabel>
                        {t('calendar.additionalDetails')}
                      </ScheduledDetailsLabel>
                      <ScheduledDetailsText>
                        {additionalDetails}
                      </ScheduledDetailsText>
                    </ScheduledDetailsBox>
                  )}

                  <InterviewTimeSelector
                    isModalOpen={isSlotSelectionModalOpen}
                    setIsModalOpen={setIsSlotSelectionModalOpen}
                    localAvailableDates={localAvailableDates}
                    selectedDateTime={selectedDateTime}
                    isSubmitting={isSubmitting}
                    handleSubmit={handleSubmit}
                    handleDecline={handleDecline}
                    handleDateClick={handleDateClick}
                  />
                </InvitationBody>
              </InvitationCard>
            )}

            <StyledRowAuto>
              <JobDetails {...jobDetailsData} />
            </StyledRowAuto>
          </StyledCol>
          <StyledColFlex>
            <Row>
              <CompanyDetails {...companyDetailsData} />
            </Row>
          </StyledColFlex>
        </StyledRow>
      </CalendarPageContent>

      <FooterContent className="d-flex py-2 border-t border-gray-200">
        {t('footer.TalentinoAI')} © {new Date().getFullYear()}{' '}
        {t('footer.allRightsReserved')}
      </FooterContent>
    </CalendarPageWrapper>
  )
}

export default Calendar
